// Canvas v2 pointer interaction. Transient gesture state lives in this
// module (not root state) so drag/resize can update DOM imperatively
// via `el.update({ style: {...} })` without cascading full re-renders.
// Final positions commit to root state on pointerup.

import { bboxOfRects } from './geom.js'

const SESSION = {
  current: null,       // active gesture { type, pointerId, ... }
  pointers: {},        // pointerId → { id, type, x, y, startX, startY }
  itemRefs: {},        // pageKey → CanvasItem element ref
  sceneRef: null,
  surfaceRef: null
}

function ensureSceneRefs(el) {
  const root = el.getRoot?.()
  if (!SESSION.surfaceRef) SESSION.surfaceRef = root?.lookdown?.('CanvasSurface') || null
  if (!SESSION.sceneRef) SESSION.sceneRef = root?.lookdown?.('CvScene') || null
}

function itemRefByKey(el, key) {
  if (SESSION.itemRefs[key]) return SESSION.itemRefs[key]
  const root = el.getRoot?.()
  const items = root?.lookdown?.('CvItems')
  if (items?.__ref?.children) {
    for (const child of Object.values(items)) {
      if (child?.state?.key === key) {
        SESSION.itemRefs[key] = child
        return child
      }
    }
  }
  return null
}

function surfaceRect(el) {
  ensureSceneRefs(el)
  return (SESSION.surfaceRef?.node || el.node).getBoundingClientRect()
}

function toCanvas(v2, clientX, clientY, rect) {
  const { x, y, scale } = v2.viewport
  return { x: (clientX - rect.left - x) / scale, y: (clientY - rect.top - y) / scale }
}

function updateItemStyle(el, key, it) {
  const ref = itemRefByKey(el, key)
  if (!ref?.update) return
  ref.update({
    style: {
      left: (it.x ?? 0) + 'px',
      top: (it.y ?? 0) + 'px',
      width: (it.w ?? 0) + 'px',
      height: (it.h ?? 0) + 'px'
    }
  })
}

// Snap lines render in the editor overlay (screen-space). `lines` are
// canvas-space — convert via current viewport.
function updateSnapLinesDOM(el, lines) {
  const overlay = el.getRoot?.()?.lookdown?.('CanvasOverlayV2')
  const layer = overlay?.CvSnapLines
  if (!layer) return
  const v2 = el.call('getCanvasV2'); const vp = v2?.viewport || { x: 0, y: 0, scale: 1 }
  for (let i = 0; i < 4; i++) {
    const line = layer[`Line${i + 1}`]
    if (!line?.update) continue
    const def = lines[i]
    if (!def) { line.update({ style: { display: 'none' } }); continue }
    const base = { display: 'block', position: 'absolute', background: 'rgba(64,156,255,1)' }
    if (def.axis === 'h') {
      const screenY = def.pos * vp.scale + vp.y
      line.update({ style: { ...base, left: '0', right: '0', top: screenY + 'px', height: '1px' } })
    } else {
      const screenX = def.pos * vp.scale + vp.x
      line.update({ style: { ...base, top: '0', bottom: '0', left: screenX + 'px', width: '1px' } })
    }
  }
}

// Anchored zoom — returns { x, y, scale } so the world-point under
// (clientCx, clientCy) stays pinned.
function anchoredZoom(viewport, clientCx, clientCy, newScale, rect) {
  const worldX = (clientCx - rect.left - viewport.x) / viewport.scale
  const worldY = (clientCy - rect.top - viewport.y) / viewport.scale
  return {
    x: (clientCx - rect.left) - worldX * newScale,
    y: (clientCy - rect.top) - worldY * newScale,
    scale: newScale
  }
}

// Capture on a stable element (the canvas surface) so pointermove
// events keep flowing even when the pointer leaves the original target
// — `ev.currentTarget` is unreliable under DOMQL's delegated handlers.
function capture(el, ev) {
  try {
    const target = SESSION.surfaceRef?.node || el?.node || ev.target
    target?.setPointerCapture?.(ev.pointerId)
  } catch {}
}

function recordPointer(ev) {
  SESSION.pointers[ev.pointerId] = {
    id: ev.pointerId, type: ev.pointerType,
    x: ev.clientX, y: ev.clientY,
    startX: ev.clientX, startY: ev.clientY
  }
}

function activePointers() { return Object.values(SESSION.pointers) }

export function trackPointerDown(ev) { recordPointer(ev) }

// Drop cached element refs for removed items.
export function forgetItemRefs(ids) {
  if (!Array.isArray(ids)) return
  for (const id of ids) delete SESSION.itemRefs[id]
}

export function trackPointerMove(ev) {
  const p = SESSION.pointers[ev.pointerId]
  // Browsers emit zero-delta pointermove on some trackpads; skip.
  if (p && p.x === ev.clientX && p.y === ev.clientY && SESSION.current) return
  if (p) { p.x = ev.clientX; p.y = ev.clientY }
  const s = SESSION.current
  if (!s) {
    if (activePointers().length === 2) this.call('beginPinch')
    return
  }
  if (s.type === 'drag') this.call('applyDragMove', ev)
  else if (s.type === 'resize') this.call('applyResizeMove', ev)
  else if (s.type === 'marquee') this.call('applyMarqueeMove', ev)
  else if (s.type === 'pan') this.call('applyPanMove', ev)
  else if (s.type === 'pinch') this.call('applyPinchMove', ev)
  else if (s.type === 'spawnGuide') this.call('applySpawnGuideMove', ev)
}

export function trackPointerEnd(ev) {
  delete SESSION.pointers[ev.pointerId]
  const s = SESSION.current
  if (!s) return
  if (s.type === 'drag' || s.type === 'resize') this.call('commitTransient')
  else if (s.type === 'marquee') this.call('commitMarquee')
  SESSION.current = null
  // Clear transient chrome imperatively (no patchCanvas → no re-render).
  updateSnapLinesDOM(this, [])
  // Marquee box hides via chrome refresh after we clear v2.marquee
  // below. Clear in-memory v2.marquee directly (already done in
  // commitMarquee) — refresh runs via patchCanvas there.
  const v2 = this.call('getCanvasV2')
  if (v2 && v2.marquee) { v2.marquee = null; this.call('refreshCanvasChrome') }
}

// ---- DRAG -------------------------------------------------------------
export function beginDrag(ev, key) {
  const v2 = this.call('getCanvasV2'); if (!v2) return
  ev.preventDefault?.(); capture(this, ev); recordPointer(ev)
  const sel = v2.selection
  const movingIds = sel.ids.includes(key) ? [...sel.ids] : [key]
  const movingSet = new Set(movingIds)
  const start = {}
  movingIds.forEach((id) => {
    const it = v2.items[id]
    if (it) start[id] = { x: it.x, y: it.y, w: it.w, h: it.h }
  })
  // Precompute snap targets once per gesture — the items list and
  // guide positions don't change during a drag.
  const snapTargets = v2.snap.enabled && v2.snap.toItems
    ? Object.entries(v2.items).filter(([id]) => !movingSet.has(id)).map(([, it]) => it)
    : []
  // Anchor the drag to the cursor's offset within the primary item so
  // the cursor stays at the same relative point throughout the drag.
  const primaryItem = v2.items[key]
  const v2Vp = v2.viewport
  const primaryScreen = primaryItem
    ? { x: primaryItem.x * v2Vp.scale + v2Vp.x, y: primaryItem.y * v2Vp.scale + v2Vp.y }
    : { x: ev.clientX, y: ev.clientY }
  const cursorOffset = { x: ev.clientX - primaryScreen.x, y: ev.clientY - primaryScreen.y }

  SESSION.current = {
    type: 'drag',
    pointerId: ev.pointerId,
    startClient: { x: ev.clientX, y: ev.clientY },
    movingIds, start, live: { ...start }, snapTargets, cursorOffset
  }
}

// Auto-pan threshold (screen px) when dragging near surface edge.
const EDGE_BAND = 40
const EDGE_PAN_PER_FRAME = 12

function maybeAutoPan(el, ev) {
  const rect = surfaceRect(el)
  let dx = 0, dy = 0
  if (ev.clientX - rect.left < EDGE_BAND) dx = EDGE_PAN_PER_FRAME
  else if (rect.right - ev.clientX < EDGE_BAND) dx = -EDGE_PAN_PER_FRAME
  if (ev.clientY - rect.top < EDGE_BAND) dy = EDGE_PAN_PER_FRAME
  else if (rect.bottom - ev.clientY < EDGE_BAND) dy = -EDGE_PAN_PER_FRAME
  if (!dx && !dy) return
  const v2 = el.call('getCanvasV2'); if (!v2) return
  const vp = v2.viewport
  el.call('applyViewport', { ...vp, x: vp.x + dx, y: vp.y + dy })
}

export function applyDragMove(ev) {
  const v2 = this.call('getCanvasV2'); const s = SESSION.current
  if (!s || s.type !== 'drag' || s.pointerId !== ev.pointerId) return
  maybeAutoPan(this, ev)
  const scale = v2.viewport.scale || 1
  const rawDx = (ev.clientX - s.startClient.x) / scale
  const rawDy = (ev.clientY - s.startClient.y) / scale

  const bbox = bboxOfRects(s.movingIds.map((id) => {
    const st = s.start[id]
    return st && { x: st.x + rawDx, y: st.y + rawDy, w: st.w, h: st.h }
  }))

  const snap = bbox ? this.call('snapRect', { rect: bbox, movingIds: s.movingIds }) : { dx: 0, dy: 0, lines: [] }

  s.movingIds.forEach((id) => {
    const st = s.start[id]; if (!st) return
    s.live[id] = { x: st.x + rawDx + snap.dx, y: st.y + rawDy + snap.dy, w: st.w, h: st.h }
    updateItemStyle(this, id, s.live[id])
    // Mirror to v2.items so refreshCanvasChrome reads live coords.
    const it = v2.items[id]
    if (it) { it.x = s.live[id].x; it.y = s.live[id].y }
  })
  updateSnapLinesDOM(this, snap.lines)
  this.call('refreshCanvasChrome')
}

// ---- RESIZE -----------------------------------------------------------
export function beginResize(ev, key, handle) {
  const v2 = this.call('getCanvasV2'); if (!v2) return
  ev.preventDefault?.(); ev.stopPropagation?.(); capture(this, ev); recordPointer(ev)
  const it = v2.items[key]; if (!it) return
  SESSION.current = {
    type: 'resize', pointerId: ev.pointerId, key, handle,
    startClient: { x: ev.clientX, y: ev.clientY },
    start: { x: it.x, y: it.y, w: it.w, h: it.h },
    live: { x: it.x, y: it.y, w: it.w, h: it.h }
  }
}

export function applyResizeMove(ev) {
  const v2 = this.call('getCanvasV2'); const s = SESSION.current
  if (!s || s.type !== 'resize' || s.pointerId !== ev.pointerId) return
  const scale = v2.viewport.scale || 1
  const dx = (ev.clientX - s.startClient.x) / scale
  const dy = (ev.clientY - s.startClient.y) / scale
  const { handle, start, key } = s
  const MIN = 80
  let nx = start.x, ny = start.y, nw = start.w, nh = start.h
  if (handle.includes('w')) { nx = start.x + dx; nw = start.w - dx }
  if (handle.includes('e')) { nw = start.w + dx }
  if (handle.includes('n')) { ny = start.y + dy; nh = start.h - dy }
  if (handle.includes('s')) { nh = start.h + dy }
  if (nw < MIN) { if (handle.includes('w')) nx = start.x + start.w - MIN; nw = MIN }
  if (nh < MIN) { if (handle.includes('n')) ny = start.y + start.h - MIN; nh = MIN }
  const snap = this.call('snapRect', { rect: { x: nx, y: ny, w: nw, h: nh }, movingIds: [key] })
  if (handle.includes('w')) { nx += snap.dx; nw -= snap.dx }
  else if (handle.includes('e')) { nw += snap.dx }
  if (handle.includes('n')) { ny += snap.dy; nh -= snap.dy }
  else if (handle.includes('s')) { nh += snap.dy }
  s.live = { x: nx, y: ny, w: nw, h: nh }
  updateItemStyle(this, key, s.live)
  // Mirror to v2.items for refreshCanvasChrome live read.
  const itLive = v2.items[key]
  if (itLive) { itLive.x = nx; itLive.y = ny; itLive.w = nw; itLive.h = nh }
  updateSnapLinesDOM(this, snap.lines || [])
  this.call('refreshCanvasChrome')
}

// ---- COMMIT -----------------------------------------------------------
export function commitTransient() {
  const s = SESSION.current; if (!s) return
  if (s.type === 'drag') {
    s.movingIds.forEach((id) => {
      const live = s.live[id]
      if (live) this.call('patchCanvasItem', id, { x: live.x, y: live.y })
    })
  } else if (s.type === 'resize') {
    this.call('patchCanvasItem', s.key, s.live)
  }
  // Items moved → reposition all chrome (selection box, handles, navbar).
  this.call('refreshCanvasChrome')
  this.call('pushHistory')
  this.call('persistCanvasLayout')
}

// ---- MARQUEE ----------------------------------------------------------
export function beginMarquee(ev) {
  const v2 = this.call('getCanvasV2'); if (!v2) return
  if (ev.button === 2) return
  if (activePointers().length >= 1) {
    recordPointer(ev)
    if (activePointers().length === 2) this.call('beginPinch')
    return
  }
  capture(this, ev); recordPointer(ev)
  const rect = surfaceRect(this)
  const p = toCanvas(v2, ev.clientX, ev.clientY, rect)
  if (!ev.shiftKey && !ev.metaKey && !ev.ctrlKey) this.call('clearSelection')
  SESSION.current = {
    type: 'marquee', pointerId: ev.pointerId,
    origin: p, current: p,
    additive: ev.shiftKey || ev.metaKey || ev.ctrlKey,
    rect
  }
  v2.marquee = { x: p.x, y: p.y, w: 0, h: 0 }
  this.call('refreshCanvasChrome')
}

export function applyMarqueeMove(ev) {
  const v2 = this.call('getCanvasV2'); const s = SESSION.current
  if (!s || s.type !== 'marquee') return
  const p = toCanvas(v2, ev.clientX, ev.clientY, s.rect)
  s.current = p
  v2.marquee = {
    x: Math.min(s.origin.x, p.x),
    y: Math.min(s.origin.y, p.y),
    w: Math.abs(p.x - s.origin.x),
    h: Math.abs(p.y - s.origin.y)
  }
  this.call('refreshCanvasChrome')
}

export function commitMarquee() {
  const v2 = this.call('getCanvasV2'); const s = SESSION.current
  if (!v2 || !s) return
  // marqueeRect for itemsInMarquee — read from session, not state.
  const rect = {
    x: Math.min(s.origin.x, s.current.x),
    y: Math.min(s.origin.y, s.current.y),
    w: Math.abs(s.current.x - s.origin.x),
    h: Math.abs(s.current.y - s.origin.y)
  }
  const hits = this.call('itemsInMarqueeRect', rect)
  if (s.additive) {
    const merged = Array.from(new Set([...(v2.selection.ids || []), ...hits]))
    this.call('selectMany', merged)
  } else {
    this.call('selectMany', hits)
  }
}

// ---- PAN --------------------------------------------------------------
export function beginPan(ev) {
  const v2 = this.call('getCanvasV2'); if (!v2) return
  capture(this, ev); recordPointer(ev)
  SESSION.current = {
    type: 'pan', pointerId: ev.pointerId,
    startClient: { x: ev.clientX, y: ev.clientY },
    startViewport: { ...v2.viewport }
  }
}

export function applyPanMove(ev) {
  const s = SESSION.current
  if (!s || s.type !== 'pan' || s.pointerId !== ev.pointerId) return
  const dx = ev.clientX - s.startClient.x
  const dy = ev.clientY - s.startClient.y
  this.call('applyViewport', { ...s.startViewport, x: s.startViewport.x + dx, y: s.startViewport.y + dy })
}

// ---- PINCH ------------------------------------------------------------
export function beginPinch() {
  const v2 = this.call('getCanvasV2'); if (!v2) return
  const pts = activePointers(); if (pts.length < 2) return
  const [a, b] = pts
  SESSION.current = {
    type: 'pinch',
    ids: [a.id, b.id],
    startDist: Math.hypot(b.x - a.x, b.y - a.y),
    startCenter: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
    startViewport: { ...v2.viewport },
    rect: surfaceRect(this)
  }
  this.call('patchCanvas', { marquee: null })
}

export function applyPinchMove() {
  const s = SESSION.current
  if (!s || s.type !== 'pinch') return
  const pts = (s.ids || []).map((id) => SESSION.pointers[id]).filter(Boolean)
  if (pts.length < 2) return
  const [a, b] = pts
  const cx = (a.x + b.x) / 2, cy = (a.y + b.y) / 2
  const dist = Math.hypot(b.x - a.x, b.y - a.y)
  const factor = Math.max(0.02, Math.min(10, dist / (s.startDist || 1)))
  const newScale = Math.max(0.035, Math.min(2.5, s.startViewport.scale * factor))
  this.call('applyViewport', anchoredZoom(s.startViewport, cx, cy, newScale, s.rect))
}

// ---- WHEEL ------------------------------------------------------------
// DOMQL's delegated wheel listener is passive, so preventDefault is a
// no-op. `overscrollBehavior: contain` on the surface suppresses the
// page-scroll side effect instead.
export function onCanvasWheel(ev) {
  const v2 = this.call('getCanvasV2'); if (!v2) return
  const rect = surfaceRect(this)
  const vp = v2.viewport
  if (ev.ctrlKey || ev.metaKey) {
    const factor = Math.exp(-ev.deltaY * 0.0025)
    const newScale = Math.max(0.035, Math.min(2.5, vp.scale * factor))
    this.call('applyViewport', anchoredZoom(vp, ev.clientX, ev.clientY, newScale, rect))
  } else {
    this.call('applyViewport', { ...vp, x: vp.x - ev.deltaX, y: vp.y - ev.deltaY })
  }
}

// ---- GUIDES -----------------------------------------------------------
// Spawn a new guide from a ruler drag. Mutates v2.guides in place +
// triggers a state.update once for re-render. The session tracks the
// new guide's index so subsequent moves re-position the same one.
export function spawnGuide(axis, ev) {
  const v2 = this.call('getCanvasV2'); if (!v2) return
  capture(this, ev); recordPointer(ev)
  const rect = surfaceRect(this)
  const p = toCanvas(v2, ev.clientX, ev.clientY, rect)
  const prev = v2.guides || { horizontal: [], vertical: [] }
  const horizontal = axis === 'h' ? [...prev.horizontal, p.y] : [...prev.horizontal]
  const vertical = axis === 'v' ? [...prev.vertical, p.x] : [...prev.vertical]
  const index = axis === 'h' ? horizontal.length - 1 : vertical.length - 1
  SESSION.current = { type: 'spawnGuide', axis, pointerId: ev.pointerId, index, rect }
  // Single state.update — re-renders CvGuidesLayer children.
  this.call('patchCanvas', { guides: { horizontal, vertical } })
}

export function applySpawnGuideMove(ev) {
  const v2 = this.call('getCanvasV2'); const s = SESSION.current
  if (!s || s.type !== 'spawnGuide' || s.pointerId !== ev.pointerId) return
  const p = toCanvas(v2, ev.clientX, ev.clientY, s.rect)
  // Mutate in place — no state.update during drag for smoothness.
  const arr = s.axis === 'h' ? v2.guides.horizontal : v2.guides.vertical
  if (s.index < 0 || s.index >= arr.length) return
  arr[s.index] = s.axis === 'h' ? p.y : p.x
  // Imperative DOM update on the matching CanvasGuide element.
  const layer = this.getRoot?.()?.lookdown?.('CvGuidesLayer')
  if (layer) {
    for (const child of Object.values(layer)) {
      if (child?.state?.axis === s.axis && child?.state?.index === s.index && child?.update) {
        // Position is canvas-space → screen via viewport for chrome rendering.
        const vp = v2.viewport
        if (s.axis === 'h') {
          const screenY = p.y * vp.scale + vp.y
          child.update({ style: { top: screenY + 'px' } })
        } else {
          const screenX = p.x * vp.scale + vp.x
          child.update({ style: { left: screenX + 'px' } })
        }
        break
      }
    }
  }
}

// Drag an EXISTING guide to reposition it.
export function beginGuideDrag(ev, axis, index) {
  const v2 = this.call('getCanvasV2'); if (!v2) return
  ev.preventDefault?.(); ev.stopPropagation?.()
  capture(this, ev); recordPointer(ev)
  const rect = surfaceRect(this)
  SESSION.current = { type: 'spawnGuide', axis, pointerId: ev.pointerId, index, rect }
}
